import React, { useState, useEffect, useCallback } from "react";
import api from "../lib/api";

// Round 6: Trial Balance view. Sums journal entries by account for the
// current company (void entries excluded server-side). Shows honestly
// whether debits equal credits instead of forcing a balance.
export default function TrialBalance() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/finance/trial-balance");
      setData(res.data);
      setError("");
    } catch (e) {
      setError("Failed to load trial balance.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const money = (n) => "$" + Number(n || 0).toFixed(2);

  return (
    <div data-testid="trial-balance" className="mt-6 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-slate-900">Trial Balance</h3>
        <button type="button" onClick={load} className="rounded-md border border-slate-200 px-3 py-1 text-sm text-slate-700 hover:bg-slate-50">Refresh</button>
      </div>
      {loading && <p className="mt-3 text-sm text-slate-500">Loading trial balance...</p>}
      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
      {data && (
        <div className="mt-3">
          <p data-testid="trial-balance-status" className={data.balanced ? "text-sm font-medium text-emerald-700" : "text-sm font-medium text-red-700"}>
            {data.balanced ? "Balanced: total debits equal total credits." : "Not balanced: difference " + money(data.difference) + ". Check unbalanced manual journal entries."}
          </p>
          <table className="mt-3 w-full text-sm">
            <thead>
              <tr className="border-b text-left text-slate-500">
                <th className="py-2 pr-2">Code</th>
                <th className="py-2 pr-2">Account</th>
                <th className="py-2 pr-2 text-right">Debit</th>
                <th className="py-2 text-right">Credit</th>
              </tr>
            </thead>
            <tbody>
              {data.accounts.map((row) => (
                <tr key={row.account_id} className="border-b border-slate-100">
                  <td className="py-2 pr-2">{row.code}</td>
                  <td className="py-2 pr-2">{row.name}</td>
                  <td className="py-2 pr-2 text-right">{money(row.debit)}</td>
                  <td className="py-2 text-right">{money(row.credit)}</td>
                </tr>
              ))}
              <tr className="font-semibold">
                <td className="py-2 pr-2" colSpan={2}>Totals</td>
                <td className="py-2 pr-2 text-right">{money(data.total_debit)}</td>
                <td className="py-2 text-right">{money(data.total_credit)}</td>
              </tr>
            </tbody>
          </table>
          {data.accounts.length === 0 && <p className="mt-3 text-sm text-slate-500">No journal activity yet for this company.</p>}
        </div>
      )}
    </div>
  );
}
